/** UI strings for the F11 models (molecular orbital, occupation); merged into the photons dictionary. */
export const sv = {
  moTitle: "Vätemolekyljonen H₂⁺: linjärkombination av två 1s-orbitaler",
  moNote:
    "Kärnorna hålls fasta på avståndet R. ψ± = C±(φa ± φb) med normeringskonstanten C± = 1/√(2(1 ± S)). Energierna är räknade från en väteatom i 1s plus en fri proton. Modellen är den enklaste LCAO-modellen och ger för grunt bindningsdjup; elektronbesättningen nederst använder samma två orbitaler för alla arter (oberoende elektroner).",
  moViewDensity: "Sannolikhetstäthet längs axeln",
  moViewEnergy: "Potentiell energi U(R)",
  moSeparation: "Kärnavstånd R (a₀)",
  moCombination: "Kombination",
  moBonding: "Bindande σg (+)",
  moAntibonding: "Antibindande σ*u (−)",
  moNuclear: "Kärnrepulsion e²/(4πε₀R)",
  moNuclearOn: "Med",
  moNuclearOff: "Utan",
  moDensitySvg: "Sannolikhetstäthet längs kärnaxeln",
  moDensityDesc:
    "Sannolikhetstätheten |ψ|² längs linjen genom båda kärnorna. Den valda kombinationen är heldragen, den andra streckad och medelvärdet av två atomtätheter utan interferens prickat. Kärnornas lägen är markerade på axeln.",
  moAxisZ: "z (a₀), mittpunkten vid 0",
  moOverlap: "Överlapp S",
  moBondingC: "C₊ (bindande)",
  moAntibondingC: "C₋ (antibindande)",
  moMidpoint: "|ψ|² i mittpunkten (a₀⁻³)",
  moMidpointReference: "(|φa|² + |φb|²)/2 i mittpunkten (a₀⁻³)",
  moMidpointRatio: "Kvot mot referensen",
  moEnergySvg: "Energikurvor för bindande och antibindande tillstånd",
  moEnergyDesc:
    "Energin som funktion av kärnavståndet R för den bindande (heldragen) och den antibindande (streckad) kombinationen, med eller utan kärnrepulsionen. Den streckade horisontella linjen är H(1s) + p. Det valda R är markerat; med kärnrepulsion visar en punkt den bindande kurvans minimum.",
  moAxisR: "R (a₀)",
  moAsymptote: "H(1s) + p",
  moPicometres: "R (pm)",
  moBondingTotal: "U₊(R) (eV)",
  moBondingElectronic: "E₊(R), utan kärnrepulsion (eV)",
  moAntibondingTotal: "U₋(R) (eV)",
  moAntibondingElectronic: "E₋(R), utan kärnrepulsion (eV)",
  moMinimumR: "Minimum för U₊: R (a₀)",
  moMinimumDepth: "Minimum för U₊: djup (eV)",
  moExact: "Exakt H₂⁺ (utanför boken): R (a₀) · djup (eV)",
  moOutside: "Valt R:s energi utanför bilden",
  moElectrons: "Antal elektroner",
  moBondOrder: "Bindningsordning (N_b − N_a)/2",
  moSpecies: "Art med denna besättning",
  moBondingCount: "Elektroner i σg",
  moAntibondingCount: "Elektroner i σ*u",
  moDiagramSvg: "Orbitaldiagram med besättning",
  moDiagramDesc:
    "Två atomära 1s-nivåer till vänster och höger, den bindande σg-nivån under och den antibindande σ*u-nivån över. Pilar visar elektroner med spinn upp och spinn ned; högst två per orbital.",
  moDensityFormula: String.raw`$S=e^{-R/a_0}\left[1+\frac{R}{a_0}+\frac{R^2}{3a_0^2}\right]$, $\;C_\pm=\dfrac{1}{\sqrt{2(1\pm S)}}$. Samma normering kräver $\int|\psi_\pm|^2\,dV=1$; korstermen $2\varphi_a\varphi_b$ flyttar sannolikhet mot (bindande) eller bort från (antibindande) området mellan kärnorna.`,
  moEnergyFormula: String.raw`$E_\pm=\dfrac{H_{aa}\pm H_{ab}}{1\pm S}$ ur $H\mathbf c=E S\mathbf c$, och $U_\pm(R)=E_\pm+\dfrac{e^2}{4\pi\epsilon_0R}$. Bindningsordning $b=(N_b-N_a)/2$.`,
  occTitle: "Besättning: räkning, samma x och Fermi–Dirac-fördelning",
  occNote:
    "Varje fullständigt enpartikeltillstånd inkluderar spinn. Räkningen förutsätter lika statistiska vikter för de tillstånd regeln tillåter; x = (E − μ)/(k_BT) gäller ett tillstånd i kontakt med en reservoar.",
  occViewCount: "Räkna tillstånd",
  occViewSameX: "Samma x",
  occViewFermi: "Fermigas",
  occParticles: "Antal partiklar N",
  occStates: "Antal fullständiga tillstånd g",
  occCopies: "Oberoende kopior",
  occCountSvg: "Besättningsvektorer för identiska partiklar",
  occCountDesc:
    "Till vänster besättningsvektorerna för identiska bosoner, till höger för identiska fermioner, för en kopia. Varje rad är ett flerpartikeltillstånd: rutorna är de fullständiga tillstånden och prickarna partiklarna.",
  occBosons: "Identiska bosoner",
  occFermions: "Identiska fermioner",
  occShown: "visar {shown} av {total}",
  occNone: "inga: fler partiklar än {states} tillstånd",
  occLabelled: "Särskiljbara: g^N",
  occOrdered: "Ordnade listor g!/(g − N)!",
  occBosonCount: "Bosoner: C(g + N − 1, N)",
  occFermionCount: "Fermioner: C(g, N)",
  occSingleLabelled: "P(inget tillstånd delas), särskiljbara",
  occSingleBosons: "P(inget tillstånd delas), bosoner",
  occSingleFermions: "P(inget tillstånd delas), fermioner",
  occCountFormula: String.raw`Antal per kopia: särskiljbara $g^N$, bosoner $\binom{g+N-1}{N}$, fermioner $\binom{g}{N}$. Oberoende kopior multipliceras. Ordnade listor $g!/(g-N)!$ räknar varje fermiontillstånd $N!$ gånger.`,
  occX: "x = (E − μ)/(k_BT)",
  occSameXSvg: "Medelbesättningstal mot x",
  occSameXDesc:
    "Medelbesättningstalet för Bose–Einstein (heldragen), Maxwell–Boltzmann (streckad) och Fermi–Dirac (heldragen, annan färg) som funktion av x, logaritmisk y-axel. Det valda x är markerat.",
  occAxisX: "x = (E − μ)/(k_BT)",
  occMB: "n̄ Maxwell–Boltzmann e^(−x)",
  occBE: "n̄ Bose–Einstein 1/(e^x − 1)",
  occFD: "n̄ Fermi–Dirac 1/(e^x + 1)",
  occBERelative: "(n̄_BE − n̄_MB)/n̄_MB",
  occFDRelative: "(n̄_FD − n̄_MB)/n̄_MB",
  occEmptyMB: "P(q = 0), Maxwell–Boltzmann",
  occEmptyBE: "P(q = 0), Bose–Einstein",
  occEmptyFD: "P(q = 0), Fermi–Dirac",
  occOutside: "Valt x utanför bilden",
  occSameXFormula: String.raw`$P(q)=e^{-qx}/\Xi$, $\;\bar n=-\partial_x\ln\Xi$. $\Xi_{\rm BE}=(1-e^{-x})^{-1}$ (kräver $x>0$), $\Xi_{\rm FD}=1+e^{-x}$, $\Xi_{\rm MB}=\exp(e^{-x})$.`,
  occConcentration: "Elektronkoncentration n (10²⁸ m⁻³)",
  occTemperature: "Temperatur T (K)",
  occProbe: "Energigräns E/E_F0",
  occPotential: "Kemisk potential",
  occFixedN: "Fast N: μ(T) löses ur N",
  occFixedMu: "Fast μ = E_F0",
  occFermiSvg: "Fermi–Dirac-fördelning och elektroner per energiintervall",
  occFermiDesc:
    "Överst f(E) och, streckad, stegfunktionen vid T = 0. Nederst g(E)f(E) fylld och g(E) streckad, per elektron och i enheter av E_F0. En punkt markerar μ där f = 1/2 och en prickad linje energigränsen.",
  occAxisF: "f(E)",
  occAxisGF: "g(E) f(E)",
  occAxisE: "E/E_F0",
  occFermiEnergy: "Fermienergi E_F0 (eV)",
  occFermiSpeed: "Fermifart v_F (m/s)",
  occFermiTemperature: "Fermitemperatur T_F = E_F0/k_B (K)",
  occPressure: "Tryck vid T = 0: 2nE_F0/5 (Pa)",
  occKT: "k_BT (eV)",
  occReduced: "k_BT/E_F0",
  occMu: "μ/E_F0",
  occNumber: "N/N₀",
  occMean: "Medelenergi per elektron / E_F0",
  occBelow: "Andel elektroner under energigränsen",
  occFProbe: "f vid energigränsen",
  occFermiFormula: String.raw`$g(E)=\dfrac{(2m)^{3/2}V}{2\pi^2\hbar^3}E^{1/2}$ (42.15), $f(E)=\dfrac{1}{e^{(E-E_F)/kT}+1}$ (42.16), $dN=g(E)f(E)\,dE$ (42.17), $E_{F0}=\dfrac{(3\pi^2)^{2/3}\hbar^2}{2m}n^{2/3}$ (42.20), $E_{\rm av}=\tfrac35E_{F0}$ (42.21). Vid fast $N$ bestäms $\mu(T)$ ur $\int_0^\infty g f\,dE=N$ (utanför boken).`,
};

export const en: Record<keyof typeof sv, string> = {
  moTitle: "The hydrogen molecule ion H₂⁺: a linear combination of two 1s orbitals",
  moNote:
    "The nuclei are held at separation R. ψ± = C±(φa ± φb) with normalisation constant C± = 1/√(2(1 ± S)). Energies are measured from a hydrogen atom in 1s plus a free proton. This is the simplest LCAO model and gives too shallow a well; the electron occupancy below uses the same two orbitals for every species (independent electrons).",
  moViewDensity: "Probability density along the axis",
  moViewEnergy: "Potential energy U(R)",
  moSeparation: "Nuclear separation R (a₀)",
  moCombination: "Combination",
  moBonding: "Bonding σg (+)",
  moAntibonding: "Antibonding σ*u (−)",
  moNuclear: "Nuclear repulsion e²/(4πε₀R)",
  moNuclearOn: "On",
  moNuclearOff: "Off",
  moDensitySvg: "Probability density along the internuclear axis",
  moDensityDesc:
    "The probability density |ψ|² along the line through both nuclei. The chosen combination is solid, the other dashed, and the mean of two atomic densities without interference dotted. The nuclear positions are marked on the axis.",
  moAxisZ: "z (a₀), midpoint at 0",
  moOverlap: "Overlap S",
  moBondingC: "C₊ (bonding)",
  moAntibondingC: "C₋ (antibonding)",
  moMidpoint: "|ψ|² at the midpoint (a₀⁻³)",
  moMidpointReference: "(|φa|² + |φb|²)/2 at the midpoint (a₀⁻³)",
  moMidpointRatio: "Ratio to the reference",
  moEnergySvg: "Energy curves of the bonding and antibonding states",
  moEnergyDesc:
    "Energy against nuclear separation R for the bonding (solid) and antibonding (dashed) combination, with or without the nuclear repulsion. The dashed horizontal line is H(1s) + p. The chosen R is marked; with repulsion a dot shows the bonding curve's minimum.",
  moAxisR: "R (a₀)",
  moAsymptote: "H(1s) + p",
  moPicometres: "R (pm)",
  moBondingTotal: "U₊(R) (eV)",
  moBondingElectronic: "E₊(R), no nuclear repulsion (eV)",
  moAntibondingTotal: "U₋(R) (eV)",
  moAntibondingElectronic: "E₋(R), no nuclear repulsion (eV)",
  moMinimumR: "Minimum of U₊: R (a₀)",
  moMinimumDepth: "Minimum of U₊: depth (eV)",
  moExact: "Exact H₂⁺ (beyond the book): R (a₀) · depth (eV)",
  moOutside: "Chosen R's energy outside the plot",
  moElectrons: "Number of electrons",
  moBondOrder: "Bond order (N_b − N_a)/2",
  moSpecies: "Species with this occupancy",
  moBondingCount: "Electrons in σg",
  moAntibondingCount: "Electrons in σ*u",
  moDiagramSvg: "Orbital diagram with occupancy",
  moDiagramDesc:
    "Two atomic 1s levels left and right, the bonding σg level below and the antibonding σ*u level above. Arrows show electrons with spin up and spin down; at most two per orbital.",
  moDensityFormula: String.raw`$S=e^{-R/a_0}\left[1+\frac{R}{a_0}+\frac{R^2}{3a_0^2}\right]$, $\;C_\pm=\dfrac{1}{\sqrt{2(1\pm S)}}$. The same normalisation requires $\int|\psi_\pm|^2\,dV=1$; the cross term $2\varphi_a\varphi_b$ moves probability into (bonding) or out of (antibonding) the region between the nuclei.`,
  moEnergyFormula: String.raw`$E_\pm=\dfrac{H_{aa}\pm H_{ab}}{1\pm S}$ from $H\mathbf c=E S\mathbf c$, and $U_\pm(R)=E_\pm+\dfrac{e^2}{4\pi\epsilon_0R}$. Bond order $b=(N_b-N_a)/2$.`,
  occTitle: "Occupation: counting, same x and the Fermi–Dirac distribution",
  occNote:
    "Each complete one-particle state includes spin. The counting assumes equal statistical weights for the states the rule allows; x = (E − μ)/(k_BT) refers to one state in contact with a reservoir.",
  occViewCount: "Count states",
  occViewSameX: "Same x",
  occViewFermi: "Fermi gas",
  occParticles: "Number of particles N",
  occStates: "Number of complete states g",
  occCopies: "Independent copies",
  occCountSvg: "Occupation vectors for identical particles",
  occCountDesc:
    "Left the occupation vectors for identical bosons, right for identical fermions, for one copy. Each row is a many-particle state: the boxes are the complete states and the dots the particles.",
  occBosons: "Identical bosons",
  occFermions: "Identical fermions",
  occShown: "showing {shown} of {total}",
  occNone: "none: more particles than {states} states",
  occLabelled: "Distinguishable: g^N",
  occOrdered: "Ordered lists g!/(g − N)!",
  occBosonCount: "Bosons: C(g + N − 1, N)",
  occFermionCount: "Fermions: C(g, N)",
  occSingleLabelled: "P(no state shared), distinguishable",
  occSingleBosons: "P(no state shared), bosons",
  occSingleFermions: "P(no state shared), fermions",
  occCountFormula: String.raw`Count per copy: distinguishable $g^N$, bosons $\binom{g+N-1}{N}$, fermions $\binom{g}{N}$. Independent copies multiply. Ordered lists $g!/(g-N)!$ count each fermion state $N!$ times.`,
  occX: "x = (E − μ)/(k_BT)",
  occSameXSvg: "Mean occupation number against x",
  occSameXDesc:
    "Mean occupation number for Bose–Einstein (solid), Maxwell–Boltzmann (dashed) and Fermi–Dirac (solid, other colour) against x, logarithmic y axis. The chosen x is marked.",
  occAxisX: "x = (E − μ)/(k_BT)",
  occMB: "n̄ Maxwell–Boltzmann e^(−x)",
  occBE: "n̄ Bose–Einstein 1/(e^x − 1)",
  occFD: "n̄ Fermi–Dirac 1/(e^x + 1)",
  occBERelative: "(n̄_BE − n̄_MB)/n̄_MB",
  occFDRelative: "(n̄_FD − n̄_MB)/n̄_MB",
  occEmptyMB: "P(q = 0), Maxwell–Boltzmann",
  occEmptyBE: "P(q = 0), Bose–Einstein",
  occEmptyFD: "P(q = 0), Fermi–Dirac",
  occOutside: "Chosen x outside the plot",
  occSameXFormula: String.raw`$P(q)=e^{-qx}/\Xi$, $\;\bar n=-\partial_x\ln\Xi$. $\Xi_{\rm BE}=(1-e^{-x})^{-1}$ (needs $x>0$), $\Xi_{\rm FD}=1+e^{-x}$, $\Xi_{\rm MB}=\exp(e^{-x})$.`,
  occConcentration: "Electron concentration n (10²⁸ m⁻³)",
  occTemperature: "Temperature T (K)",
  occProbe: "Energy limit E/E_F0",
  occPotential: "Chemical potential",
  occFixedN: "Fixed N: μ(T) solved from N",
  occFixedMu: "Fixed μ = E_F0",
  occFermiSvg: "Fermi–Dirac distribution and electrons per energy interval",
  occFermiDesc:
    "Top f(E) and, dashed, the step function at T = 0. Bottom g(E)f(E) filled and g(E) dashed, per electron and in units of E_F0. A dot marks μ where f = 1/2 and a dotted line the energy limit.",
  occAxisF: "f(E)",
  occAxisGF: "g(E) f(E)",
  occAxisE: "E/E_F0",
  occFermiEnergy: "Fermi energy E_F0 (eV)",
  occFermiSpeed: "Fermi speed v_F (m/s)",
  occFermiTemperature: "Fermi temperature T_F = E_F0/k_B (K)",
  occPressure: "Pressure at T = 0: 2nE_F0/5 (Pa)",
  occKT: "k_BT (eV)",
  occReduced: "k_BT/E_F0",
  occMu: "μ/E_F0",
  occNumber: "N/N₀",
  occMean: "Mean energy per electron / E_F0",
  occBelow: "Fraction of electrons below the energy limit",
  occFProbe: "f at the energy limit",
  occFermiFormula: String.raw`$g(E)=\dfrac{(2m)^{3/2}V}{2\pi^2\hbar^3}E^{1/2}$ (42.15), $f(E)=\dfrac{1}{e^{(E-E_F)/kT}+1}$ (42.16), $dN=g(E)f(E)\,dE$ (42.17), $E_{F0}=\dfrac{(3\pi^2)^{2/3}\hbar^2}{2m}n^{2/3}$ (42.20), $E_{\rm av}=\tfrac35E_{F0}$ (42.21). At fixed $N$, $\mu(T)$ follows from $\int_0^\infty g f\,dE=N$ (beyond the book).`,
};
